import { z } from "zod";
import {
  normalizeWatchCanonicalListingIdentity,
  type WatchCanonicalListingIdentity,
} from "./watchListingIdentity";
import { canonicalRatioToPercent } from "./watchRatioPresentation";

/** Strict runtime verification for customer-safe, server-owned recipe previews. */
const tickerSchema = z.string().regex(/^[A-Z][A-Z0-9.-]{0,14}$/);
const micCodeSchema = z.string().regex(/^[A-Z0-9]{4}$/);
const positiveIntegerSchema = z.number().int().safe().positive();
const nonNegativeIntegerSchema = z.number().int().safe().nonnegative();
const decimalSchema = z.string().regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/);

const watchPolicySchema = z.object({
  max_duration_days: positiveIntegerSchema,
  evaluation_interval_minutes: positiveIntegerSchema,
  max_alerts_per_watch: positiveIntegerSchema,
  cooldown_minutes: nonNegativeIntegerSchema,
  market_hours_only: z.boolean(),
}).strict();

/**
 * The server echoes the normalized Watch policy in the preview, which carries
 * the customer schedule fields plus the server-owned guardrail defaults that
 * are part of the documented normalized policy. Only these exact extra keys
 * are tolerated; the customer fields are still compared against the draft.
 */
const serverNormalizedPolicySchema = watchPolicySchema.extend({
  guardrail_block_conflict_levels: z.array(z.string()).optional(),
  guardrail_reeval_on_event_risk: z.boolean().optional(),
  guardrail_reeval_interval_minutes: nonNegativeIntegerSchema.optional(),
}).strict();

const canonicalListingSelectorSchema = z.object({
  ticker: tickerSchema,
  mic_code: micCodeSchema,
  asset_type: z.enum(["COMMON_STOCK", "ETF"]),
  country_code: z.literal("US"),
}).strict();

const recipeDraftBase = {
  schema_version: z.literal(1),
  job: z.literal("MARKET"),
  subject_scope: z.literal("UNDERLYING"),
  selectors: canonicalListingSelectorSchema,
  policy: watchPolicySchema,
};

const trendConfirmationRecipeDraftSchema = z.object({
  recipe_watch_draft: z.object({
    ...recipeDraftBase,
    recipe_id: z.literal("market.trend_confirmation"),
    definition_version: z.literal(1),
  }).strict(),
}).strict();

const percentageParameter = (minimum: number, maximum: number) => decimalSchema.refine(
  (value) => Number(value) >= minimum && Number(value) <= maximum,
);

const optionsRecipeParametersSchema = z.object({
  minimum_iv: percentageParameter(0.01, 5),
  maximum_relative_spread: percentageParameter(0.001, 1),
}).strict();

const optionsRecipeDraftSchema = z.object({
  recipe_watch_draft: z.object({
    ...recipeDraftBase,
    recipe_id: z.literal("market.options_volatility_liquidity_trend"),
    definition_version: z.literal(1),
    parameters: optionsRecipeParametersSchema,
  }).strict(),
}).strict();

const recipeDraftSchema = z.union([
  trendConfirmationRecipeDraftSchema,
  optionsRecipeDraftSchema,
]);

const trendConfirmationRecipePreviewSchema = z.object({
  kind: z.literal("RECIPE_WATCH"),
  job: z.literal("MARKET"),
  recipe: z.object({
    recipe_id: z.literal("market.trend_confirmation"),
    definition_version: z.literal(1),
    name: z.literal("Trend confirmation"),
    summary: z.literal("Price is above its 50-session average and RSI is at least 50."),
  }).strict(),
  subject: z.object({ ticker: tickerSchema }).strict(),
  logic: z.object({ operator: z.literal("ALL"), label: z.literal("All conditions") }).strict(),
  conditions: z.tuple([
    z.object({
      leaf_id: z.literal("close_above_sma_50"),
      signal_id: z.literal("underlying.completed_regular_close.relative_to_sma"),
      definition_version: z.literal(1),
      operator: z.literal("gt"),
      unit: z.literal("DECIMAL"),
      parameters: z.object({ direction: z.literal("ABOVE"), window: z.literal("50") }).strict(),
      sentence: z.literal("The completed daily close is above its 50-session simple moving average."),
    }).strict(),
    z.object({
      leaf_id: z.literal("rsi_at_or_above_50"),
      signal_id: z.literal("underlying.rsi.relative_to_level"),
      definition_version: z.literal(1),
      operator: z.literal("gte"),
      unit: z.literal("INDEX_POINTS"),
      parameters: z.object({ direction: z.literal("ABOVE"), level: z.literal("50") }).strict(),
      threshold_parameter_id: z.literal("level"),
      threshold: z.literal("50"),
      sentence: z.literal("Completed-daily RSI is at or above 50 index points."),
    }).strict(),
  ]),
  evidence_time: z.object({
    policy: z.literal("SAME_COMPLETED_REGULAR_SESSION"),
    display: z.literal("Same completed regular market session"),
  }).strict(),
  sentence: z.string(),
  behavior: z.literal("All conditions must be true for the same completed regular market session. The first eligible matching session can alert."),
  schedule: z.literal("After each completed market day"),
  policy: serverNormalizedPolicySchema,
}).strict();

const optionsRecipePreviewSchema = z.object({
  kind: z.literal("RECIPE_WATCH"),
  job: z.literal("MARKET"),
  recipe: z.object({
    recipe_id: z.literal("market.options_volatility_liquidity_trend"),
    definition_version: z.literal(1),
    name: z.literal("Volatility, liquidity & trend"),
    summary: z.literal("Implied volatility is elevated, option spreads are within your limit, price is above its 50-session average, and RSI is at least 50."),
  }).strict(),
  subject: z.object({ ticker: tickerSchema }).strict(),
  logic: z.object({ operator: z.literal("ALL"), label: z.literal("All conditions") }).strict(),
  parameters: z.tuple([
    z.object({
      parameter_id: z.literal("minimum_iv"), value: percentageParameter(0.01, 5), unit: z.literal("RATIO"),
      label: z.literal("Minimum implied volatility"), display_value: z.string(),
    }).strict(),
    z.object({
      parameter_id: z.literal("maximum_relative_spread"), value: percentageParameter(0.001, 1), unit: z.literal("RATIO"),
      label: z.literal("Maximum relative spread"), display_value: z.string(),
    }).strict(),
  ]),
  conditions: z.tuple([
    z.object({
      leaf_id: z.literal("atm30_iv_at_or_above_minimum"),
      signal_id: z.literal("options_chain.atm_iv.standardized_30d.relative_to_level"),
      definition_version: z.literal(1), operator: z.literal("gte"), unit: z.literal("RATIO"),
      parameters: z.object({ direction: z.literal("ABOVE"), level: percentageParameter(0.01, 5) }).strict(),
      threshold_parameter_id: z.literal("level"), threshold: percentageParameter(0.01, 5), sentence: z.string(),
    }).strict(),
    z.object({
      leaf_id: z.literal("atm_median_relative_spread_at_or_below_maximum"),
      signal_id: z.literal("options_chain.atm_median_relative_spread.standardized_30d.relative_to_level"),
      definition_version: z.literal(1), operator: z.literal("lte"), unit: z.literal("RATIO"),
      parameters: z.object({ direction: z.literal("BELOW"), level: percentageParameter(0.001, 1) }).strict(),
      threshold_parameter_id: z.literal("level"), threshold: percentageParameter(0.001, 1), sentence: z.string(),
    }).strict(),
    z.object({
      leaf_id: z.literal("close_above_sma_50"), signal_id: z.literal("underlying.completed_regular_close.relative_to_sma"),
      definition_version: z.literal(1), operator: z.literal("gt"), unit: z.literal("DECIMAL"),
      parameters: z.object({ direction: z.literal("ABOVE"), window: z.literal("50") }).strict(),
      sentence: z.literal("The completed daily close is above its 50-session simple moving average."),
    }).strict(),
    z.object({
      leaf_id: z.literal("rsi_at_or_above_50"), signal_id: z.literal("underlying.rsi.relative_to_level"),
      definition_version: z.literal(1), operator: z.literal("gte"), unit: z.literal("INDEX_POINTS"),
      parameters: z.object({ direction: z.literal("ABOVE"), level: z.literal("50") }).strict(),
      threshold_parameter_id: z.literal("level"), threshold: z.literal("50"),
      sentence: z.literal("Completed-daily RSI is at or above 50 index points."),
    }).strict(),
  ]),
  evidence_time: z.object({
    policy: z.literal("LIVE_CHAIN_PLUS_LATEST_COMPLETED_SESSION"),
    display: z.literal("Current options conditions plus the latest completed regular market session"),
  }).strict(),
  sentence: z.string(),
  behavior: z.literal("All four conditions must be true using a current eligible options-chain observation and the latest eligible completed regular market session. Unavailable evidence cannot alert or reset the Watch."),
  schedule: z.literal("During regular market hours and after completed-session evidence updates"),
  policy: serverNormalizedPolicySchema,
}).strict();

const recipePreviewSchema = z.union([
  trendConfirmationRecipePreviewSchema,
  optionsRecipePreviewSchema,
]);

export type TrendConfirmationRecipeDraftV1 = z.infer<typeof trendConfirmationRecipeDraftSchema>;
export type WatchRecipeDraftV1 = z.infer<typeof recipeDraftSchema>;
export type WatchRecipeCreationPreview = z.infer<typeof recipePreviewSchema>;

export interface WatchRecipeDraftInput {
  recipeId: string;
  definitionVersion: number;
  ticker: string;
  listingIdentity: WatchCanonicalListingIdentity | null;
  parameters?: Readonly<Record<string, string>>;
  policy: {
    maxDurationDays: number;
    evaluationIntervalMinutes: number;
    maxAlertsPerWatch: number;
    cooldownMinutes: number;
    marketHoursOnly: boolean;
  };
}

/** Build only a reviewed readable draft; recipe leaves remain server-owned. */
export function buildWatchRecipeDraft(input: WatchRecipeDraftInput): WatchRecipeDraftV1 {
  const listing = normalizeWatchCanonicalListingIdentity(input.listingIdentity);
  const candidate = {
    recipe_watch_draft: {
      schema_version: 1,
      job: "MARKET",
      recipe_id: input.recipeId,
      definition_version: input.definitionVersion,
      subject_scope: "UNDERLYING",
      selectors: { ticker: input.ticker.trim().toUpperCase(), ...(listing ?? {}) },
      ...(input.recipeId === "market.options_volatility_liquidity_trend"
        ? { parameters: input.parameters }
        : {}),
      policy: {
        max_duration_days: input.policy.maxDurationDays,
        evaluation_interval_minutes: input.policy.evaluationIntervalMinutes,
        max_alerts_per_watch: input.policy.maxAlertsPerWatch,
        cooldown_minutes: input.policy.cooldownMinutes,
        market_hours_only: input.policy.marketHoursOnly,
      },
    },
  };
  const parsed = recipeDraftSchema.safeParse(candidate);
  if (!parsed.success) {
    throw new Error("Choose an eligible active U.S. stock or ETF and valid recipe limits.");
  }
  return parsed.data;
}

/** Backward-compatible named builder for the first reviewed recipe. */
export const buildTrendConfirmationRecipeDraft = buildWatchRecipeDraft;

function policiesMatch(expected: z.infer<typeof watchPolicySchema>, actual: z.infer<typeof watchPolicySchema>) {
  return Object.entries(expected).every(([key, value]) => actual[key as keyof typeof actual] === value);
}

/** Verify the entire reviewed preview against the exact submitted readable draft. */
export function isMatchingWatchRecipeCreationPreview(preview: unknown, submittedPayload: unknown): preview is WatchRecipeCreationPreview {
  const submitted = recipeDraftSchema.safeParse(submittedPayload);
  const received = recipePreviewSchema.safeParse(preview);
  if (!submitted.success || !received.success) return false;
  const expected = submitted.data.recipe_watch_draft;
  const actual = received.data;
  if (actual.recipe.recipe_id !== expected.recipe_id
    || actual.recipe.definition_version !== expected.definition_version
    || actual.subject.ticker !== expected.selectors.ticker
    || !policiesMatch(expected.policy, actual.policy)) return false;

  if (expected.recipe_id === "market.trend_confirmation"
    && actual.recipe.recipe_id === "market.trend_confirmation") {
    return actual.sentence === `Alert me when ${expected.selectors.ticker}'s completed daily close is above its 50-session simple moving average and completed-daily RSI is at or above 50 index points on the same completed regular market session.`;
  }
  if (expected.recipe_id !== "market.options_volatility_liquidity_trend"
    || actual.recipe.recipe_id !== "market.options_volatility_liquidity_trend") return false;
  const optionsActualResult = optionsRecipePreviewSchema.safeParse(actual);
  if (!optionsActualResult.success) return false;
  const optionsActual = optionsActualResult.data;

  const iv = expected.parameters.minimum_iv;
  const spread = expected.parameters.maximum_relative_spread;
  const ivDisplay = `${canonicalRatioToPercent(iv)}%`;
  const spreadDisplay = `${canonicalRatioToPercent(spread)}%`;
  return optionsActual.parameters[0].value === iv
    && optionsActual.parameters[0].display_value === ivDisplay
    && optionsActual.parameters[1].value === spread
    && optionsActual.parameters[1].display_value === spreadDisplay
    && optionsActual.conditions[0].threshold === iv
    && optionsActual.conditions[0].parameters.level === iv
    && optionsActual.conditions[0].sentence === `Standardized 30-day implied volatility is at or above ${ivDisplay}.`
    && optionsActual.conditions[1].threshold === spread
    && optionsActual.conditions[1].parameters.level === spread
    && optionsActual.conditions[1].sentence === `The coverage-qualified median relative spread is at or below ${spreadDisplay}.`
    && optionsActual.sentence === `Alert me when ${expected.selectors.ticker}'s standardized 30-day implied volatility is at or above ${ivDisplay}, its coverage-qualified median relative option spread is at or below ${spreadDisplay}, and its latest completed daily close is above its 50-session simple moving average with RSI at or above 50.`;
}
